import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { adminApi } from '../../services/api';
import { PageTooltip } from '../components/TooltipSystem';
import {
  Sparkles, Loader2, ArrowUpRight, Clock, Target, Users, TrendingUp,
  DollarSign, RefreshCw, ChevronDown, ChevronUp, AlertTriangle,
  Check, Zap, Shield, Rocket, MessageCircle, Euro,
  Play, CheckCircle2, Circle, ExternalLink, Trash2, ListChecks,
} from 'lucide-react';

const PRIORITY_CONFIG = {
  alta: { color: '#EF4444', bg: '#FEF2F2', label: 'Alta' },
  media: { color: '#F59E0B', bg: '#FFFBEB', label: 'Media' },
  bassa: { color: '#3B82F6', bg: '#EFF6FF', label: 'Bassa' },
};

const CATEGORY_CONFIG = {
  acquisizione: { color: '#8B5CF6', icon: Users, label: 'Acquisizione' },
  retention: { color: '#10B981', icon: RefreshCw, label: 'Retention' },
  engagement: { color: '#F59E0B', icon: Target, label: 'Engagement' },
  revenue: { color: '#EF4444', icon: DollarSign, label: 'Revenue' },
  comunicazione: { color: '#3B82F6', icon: Sparkles, label: 'Comunicazione' },
  stagionale: { color: '#EC4899', icon: Clock, label: 'Stagionale' },
};

const PROFILE_STYLES = {
  base: { icon: Shield, color: '#3ECF8E', gradient: 'from-emerald-50 to-green-50', border: 'border-emerald-200', tag: 'Risparmio' },
  moderato: { icon: Zap, color: '#2196F3', gradient: 'from-blue-50 to-sky-50', border: 'border-blue-200', tag: 'Consigliato' },
  aggressivo: { icon: Rocket, color: '#FF9800', gradient: 'from-orange-50 to-amber-50', border: 'border-orange-200', tag: 'Max Engagement' },
};

const AUTOMATION_LABELS = {
  welcome_message: 'Benvenuto', purchase_notification: 'Notifica punti',
  redeem_notification: 'Riscatto premi', referral_complete_notification: 'Referral completato',
  gift_points_notification: 'Regalo punti', challenge_completed_notification: 'Challenge completata',
  birthday: 'Compleanno', welcome_followup: 'Follow-up benvenuto',
  inactive: 'Clienti inattivi', reward_reminder: 'Promemoria premi',
  tier_reminder: 'Promemoria tier', unclaimed_reward: 'Premi non ritirati',
  tier_benefit_reminder: 'Benefici tier', second_visit_month: '2a visita del mese',
  tenth_visit_year: '10a visita anno', review_request: 'Richiesta recensione',
  expiry_warning: 'Scadenza punti',
};

export default function AdminStrategy() {
  return (
    <div className="p-6 md:p-8 lg:p-10 max-w-5xl mx-auto space-y-10" data-testid="admin-strategy-page">
      <AIStrategySection />
      <InitiativesSection />
      <div className="border-t border-slate-200 pt-8">
        <MarketingSetupSection />
      </div>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   AI STRATEGY SECTION
   ═══════════════════════════════════════════════════════════════════════ */
function AIStrategySection() {
  const [suggestions, setSuggestions] = useState(null);
  const [dataSummary, setDataSummary] = useState(null);
  const [generating, setGenerating] = useState(false);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [generatedAt, setGeneratedAt] = useState(null);
  const [autoGenerated, setAutoGenerated] = useState(false);
  const [appliedTitles, setAppliedTitles] = useState(new Set());

  useEffect(() => {
    adminApi.get('/admin/ai/strategy/history?limit=3')
      .then(r => {
        const h = r.data.history || [];
        setHistory(h);
        if (h.length > 0) {
          setSuggestions(h[0].suggestions);
          setDataSummary(h[0].data_summary);
          setGeneratedAt(h[0].created_at);
          setAutoGenerated(h[0].source === 'weekly_auto');
        }
      })
      .catch(() => {});
    // Load applied titles
    adminApi.get('/admin/ai/initiatives')
      .then(r => {
        const titles = new Set((r.data.initiatives || []).map(i => i.suggestion?.title));
        setAppliedTitles(titles);
      })
      .catch(() => {});
  }, []);

  const generate = async () => {
    setGenerating(true);
    try {
      const r = await adminApi.post('/admin/ai/strategy');
      setSuggestions(r.data.suggestions);
      setDataSummary(r.data.data_summary);
      setGeneratedAt(r.data.generated_at);
      setAutoGenerated(false);
      toast.success('Strategia AI generata!');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nella generazione');
    } finally {
      setGenerating(false);
    }
  };

  const handleApply = useCallback(async (suggestion) => {
    try {
      await adminApi.post('/admin/ai/apply', { suggestion });
      setAppliedTitles(prev => new Set([...prev, suggestion.title]));
      toast.success('Iniziativa creata! Scorri in basso per la checklist.');
      // Trigger initiative list refresh via custom event
      window.dispatchEvent(new Event('initiatives-refresh'));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore nell\'applicazione');
    }
  }, []);

  return (
    <div>
      <div className="flex items-start justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold text-[#1A1A1A] flex items-center gap-2">
            <Sparkles size={24} className="text-[#D44E20]" /> Strategia Marketing
          </h1>
          <p className="text-[#64748B] text-sm mt-1">Consulenza AI personalizzata basata sui dati del tuo locale</p>
        </div>
        <button
          onClick={generate}
          disabled={generating}
          data-testid="generate-strategy-btn"
          className="flex items-center gap-2 px-5 py-3 bg-[#D44E20] text-white rounded-xl font-semibold hover:bg-[#b8411a] transition-all disabled:opacity-60 active:scale-[0.97]"
        >
          {generating ? <><Loader2 size={16} className="animate-spin" /> Analisi in corso...</> :
            <><Sparkles size={16} /> Genera suggerimenti</>}
        </button>
      </div>

      {/* Data Summary KPIs */}
      {dataSummary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          <MiniKpi icon={Users} label="Clienti totali" value={dataSummary.total_customers} color="#3B82F6" />
          <MiniKpi icon={TrendingUp} label="Attivi (30gg)" value={dataSummary.active_30d} color="#10B981" />
          <MiniKpi icon={DollarSign} label="Ricavi 30gg" value={`€${dataSummary.revenue_30d}`} color="#F59E0B" />
          <MiniKpi icon={AlertTriangle} label="A rischio" value={dataSummary.at_risk} color="#EF4444" />
        </div>
      )}

      {/* Suggestions */}
      {generating && (
        <div className="bg-white rounded-2xl border border-slate-100 p-12 text-center shadow-sm" data-testid="strategy-loading">
          <Loader2 size={40} className="animate-spin text-[#D44E20] mx-auto mb-4" />
          <p className="text-[#1A1A1A] font-semibold">Claude sta analizzando i dati del locale...</p>
          <p className="text-[#64748B] text-sm mt-1">Questo potrebbe richiedere 10-20 secondi</p>
        </div>
      )}

      {!generating && suggestions && suggestions.length > 0 && (
        <div className="space-y-4" data-testid="strategy-suggestions">
          {generatedAt && (
            <div className="flex items-center gap-2 text-xs text-[#64748B]">
              <Clock size={12} /> Generato: {new Date(generatedAt).toLocaleString('it-IT')}
              {autoGenerated && <span className="bg-blue-50 text-blue-600 text-[10px] font-semibold px-2 py-0.5 rounded-full">Automatico settimanale</span>}
            </div>
          )}
          {suggestions.map((s, i) => <SuggestionCard key={i} suggestion={s} index={i} onApply={handleApply} applied={appliedTitles.has(s.title)} />)}
        </div>
      )}

      {!generating && !suggestions && (
        <div className="bg-white rounded-2xl border border-slate-100 p-10 text-center shadow-sm" data-testid="strategy-empty">
          <Sparkles size={48} className="mx-auto mb-4 text-[#D44E20]/30" />
          <p className="text-[#1A1A1A] font-semibold mb-2">Nessuna analisi ancora generata</p>
          <p className="text-[#64748B] text-sm">Clicca "Genera suggerimenti" per ricevere una consulenza AI personalizzata</p>
        </div>
      )}

      {/* History */}
      {history.length > 1 && (
        <div className="mt-6">
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="flex items-center gap-2 text-sm text-[#64748B] hover:text-[#64748B] transition-colors"
            data-testid="toggle-history-btn"
          >
            {showHistory ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
            Storico analisi precedenti ({history.length})
          </button>
          {showHistory && (
            <div className="mt-3 space-y-3">
              {history.slice(1).map((h, i) => (
                <div key={i}
                  onClick={() => { setSuggestions(h.suggestions); setDataSummary(h.data_summary); setGeneratedAt(h.created_at); }}
                  className="bg-gray-50 rounded-xl p-4 cursor-pointer hover:bg-slate-100 transition-colors"
                  data-testid={`history-item-${i}`}
                >
                  <div className="flex items-center justify-between">
                    <span className="text-sm font-medium text-[#1A1A1A]">{h.suggestions?.length || 0} suggerimenti</span>
                    <span className="text-xs text-[#64748B]">{new Date(h.created_at).toLocaleString('it-IT')}</span>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function SuggestionCard({ suggestion: s, index, onApply, applied }) {
  const [expanded, setExpanded] = useState(index < 2);
  const [applying, setApplying] = useState(false);
  const prio = PRIORITY_CONFIG[s.priority] || PRIORITY_CONFIG.media;
  const cat = CATEGORY_CONFIG[s.category] || CATEGORY_CONFIG.engagement;
  const CatIcon = cat.icon;

  const handleApply = async (e) => {
    e.stopPropagation();
    setApplying(true);
    await onApply(s);
    setApplying(false);
  };

  return (
    <div className="bg-white rounded-2xl border border-slate-100 shadow-sm overflow-hidden hover:shadow-md transition-shadow" data-testid={`suggestion-card-${index}`}>
      <div
        onClick={() => setExpanded(!expanded)}
        className="flex items-start gap-4 p-5 cursor-pointer"
      >
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 mt-0.5" style={{ backgroundColor: `${cat.color}12` }}>
          <CatIcon size={18} style={{ color: cat.color }} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap mb-1">
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full" style={{ backgroundColor: prio.bg, color: prio.color }}>{prio.label}</span>
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-[#64748B]">{cat.label}</span>
            {applied && <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-green-50 text-green-600 flex items-center gap-0.5"><CheckCircle2 size={8} /> Applicato</span>}
          </div>
          <h3 className="text-sm font-bold text-[#1A1A1A] leading-snug">{s.title}</h3>
        </div>
        <div className="flex-shrink-0 text-[#ccc]">
          {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
        </div>
      </div>

      {expanded && (
        <div className="px-5 pb-5 border-t border-gray-50 pt-4 ml-14">
          <p className="text-sm text-[#555] leading-relaxed mb-4">{s.description}</p>
          {s.impact && (
            <div className="flex items-start gap-2 bg-green-50 rounded-xl p-3 mb-3">
              <ArrowUpRight size={14} className="text-green-600 mt-0.5 flex-shrink-0" />
              <p className="text-xs font-medium text-green-700">{s.impact}</p>
            </div>
          )}
          {s.action && (
            <div className="flex items-start gap-2 bg-[#D44E20]/5 rounded-xl p-3 mb-4">
              <Target size={14} className="text-[#D44E20] mt-0.5 flex-shrink-0" />
              <p className="text-xs font-medium text-[#D44E20]">{s.action}</p>
            </div>
          )}
          {!applied ? (
            <button onClick={handleApply} disabled={applying} data-testid={`apply-suggestion-${index}`}
              className="flex items-center gap-2 px-4 py-2.5 bg-[#111114] text-white rounded-xl text-sm font-semibold hover:bg-[#333] transition-all disabled:opacity-50 active:scale-[0.97]">
              {applying ? <><Loader2 size={14} className="animate-spin" /> Applicando...</> :
                <><Play size={14} /> Applica suggerimento</>}
            </button>
          ) : (
            <div className="flex items-center gap-2 text-green-600 text-sm font-semibold">
              <CheckCircle2 size={16} /> Iniziativa creata — controlla la checklist sotto
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   INITIATIVES SECTION (Applied suggestions with checklists)
   ═══════════════════════════════════════════════════════════════════════ */
function InitiativesSection() {
  const [initiatives, setInitiatives] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadInitiatives = useCallback(() => {
    adminApi.get('/admin/ai/initiatives')
      .then(r => setInitiatives(r.data.initiatives || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    loadInitiatives();
    const handler = () => loadInitiatives();
    window.addEventListener('initiatives-refresh', handler);
    return () => window.removeEventListener('initiatives-refresh', handler);
  }, [loadInitiatives]);

  const toggleCheck = async (initId, itemIdx) => {
    try {
      const r = await adminApi.put(`/admin/ai/initiatives/${initId}/check/${itemIdx}`);
      setInitiatives(prev => prev.map(init =>
        init.id === initId ? { ...init, checklist: r.data.checklist, progress: r.data.progress, status: r.data.status } : init
      ));
    } catch {
      toast.error('Errore aggiornamento checklist');
    }
  };

  const deleteInitiative = async (initId) => {
    try {
      await adminApi.delete(`/admin/ai/initiatives/${initId}`);
      setInitiatives(prev => prev.filter(i => i.id !== initId));
      toast.success('Iniziativa eliminata');
    } catch {
      toast.error('Errore eliminazione');
    }
  };

  if (loading) return null;
  if (initiatives.length === 0) return null;

  const active = initiatives.filter(i => i.status === 'active');
  const completed = initiatives.filter(i => i.status === 'completed');

  return (
    <div className="border-t border-slate-200 pt-8" data-testid="initiatives-section">
      <div className="flex items-center justify-between mb-5">
        <div>
          <h2 className="text-xl font-bold text-[#1A1A1A] flex items-center gap-2">
            <ListChecks size={20} className="text-[#D44E20]" /> Iniziative in corso
          </h2>
          <p className="text-[#64748B] text-sm mt-0.5">{active.length} attive, {completed.length} completate</p>
        </div>
      </div>

      <div className="space-y-4">
        {active.map(init => <InitiativeCard key={init.id} initiative={init} onToggle={toggleCheck} onDelete={deleteInitiative} />)}
        {completed.length > 0 && (
          <details className="group">
            <summary className="text-xs font-semibold text-[#64748B] cursor-pointer hover:text-[#555] mb-2">
              Completate ({completed.length})
            </summary>
            <div className="space-y-3 mt-2">
              {completed.map(init => <InitiativeCard key={init.id} initiative={init} onToggle={toggleCheck} onDelete={deleteInitiative} />)}
            </div>
          </details>
        )}
      </div>
    </div>
  );
}

function InitiativeCard({ initiative: init, onToggle, onDelete }) {
  const [expanded, setExpanded] = useState(init.status === 'active');
  const cat = CATEGORY_CONFIG[init.category] || CATEGORY_CONFIG.engagement;
  const CatIcon = cat.icon;
  const pct = init.total_steps > 0 ? Math.round((init.progress / init.total_steps) * 100) : 0;
  const isComplete = init.status === 'completed';

  return (
    <div className={`bg-white rounded-2xl border shadow-sm overflow-hidden ${isComplete ? 'border-green-200 opacity-75' : 'border-slate-100'}`}
      data-testid={`initiative-card-${init.id}`}>
      <div onClick={() => setExpanded(!expanded)} className="flex items-center gap-4 p-4 cursor-pointer">
        <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0" style={{ backgroundColor: `${cat.color}12` }}>
          {isComplete ? <CheckCircle2 size={18} className="text-green-500" /> : <CatIcon size={18} style={{ color: cat.color }} />}
        </div>
        <div className="flex-1 min-w-0">
          <h4 className="text-sm font-bold text-[#1A1A1A] truncate">{init.suggestion?.title}</h4>
          <div className="flex items-center gap-3 mt-1">
            <span className="text-[10px] font-semibold px-2 py-0.5 rounded-full bg-slate-100 text-[#64748B]">{cat.label}</span>
            <div className="flex items-center gap-1.5 flex-1">
              <div className="flex-1 h-1.5 bg-slate-100 rounded-full overflow-hidden max-w-[120px]">
                <div className="h-full rounded-full transition-all" style={{ width: `${pct}%`, backgroundColor: isComplete ? '#10B981' : cat.color }} />
              </div>
              <span className="text-[10px] font-bold text-[#64748B] tabular-nums">{init.progress}/{init.total_steps}</span>
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-shrink-0">
          {init.created_resource && (
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-blue-50 text-blue-600">
              {init.created_resource.type === 'challenge' ? 'Challenge' : 'Coupon'} creato
            </span>
          )}
          {expanded ? <ChevronUp size={14} className="text-[#ccc]" /> : <ChevronDown size={14} className="text-[#ccc]" />}
        </div>
      </div>

      {expanded && (
        <div className="px-4 pb-4 border-t border-gray-50 pt-3">
          {/* Created resource info */}
          {init.created_resource && (
            <div className="flex items-center gap-2 bg-blue-50 rounded-xl p-3 mb-3 text-xs">
              <Check size={14} className="text-blue-600" />
              <span className="text-blue-700 font-medium">
                {init.created_resource.type === 'challenge' ? 'Challenge' : 'Coupon'}: {init.created_resource.name}
                {init.created_resource.code && <span className="ml-1 font-mono bg-blue-100 px-1.5 py-0.5 rounded">{init.created_resource.code}</span>}
              </span>
              <a href={init.created_resource.link} className="ml-auto text-blue-600 hover:text-blue-800">
                <ExternalLink size={12} />
              </a>
            </div>
          )}

          {/* Checklist */}
          <div className="space-y-1">
            {init.checklist.map((item, i) => (
              <div key={i}
                onClick={(e) => { e.stopPropagation(); onToggle(init.id, item.idx); }}
                data-testid={`checklist-item-${init.id}-${item.idx}`}
                className={`flex items-center gap-3 p-2.5 rounded-xl cursor-pointer transition-colors ${item.done ? 'bg-green-50' : 'bg-gray-50 hover:bg-slate-100'}`}>
                <div className="flex-shrink-0">
                  {item.done ? <CheckCircle2 size={16} className="text-green-500" /> : <Circle size={16} className="text-[#ccc]" />}
                </div>
                <span className={`text-xs flex-1 ${item.done ? 'line-through text-[#aaa]' : 'text-[#333] font-medium'}`}>
                  {item.label}
                </span>
                {item.link && !item.done && (
                  <a href={item.link} onClick={e => e.stopPropagation()} className="text-[#D44E20] hover:text-[#b8411a] flex-shrink-0" data-testid={`checklist-link-${init.id}-${item.idx}`}>
                    <ExternalLink size={12} />
                  </a>
                )}
              </div>
            ))}
          </div>

          {/* Actions */}
          <div className="flex items-center justify-between mt-3 pt-3 border-t border-gray-50">
            <span className="text-[10px] text-[#64748B]">{new Date(init.created_at).toLocaleString('it-IT')}</span>
            <button onClick={(e) => { e.stopPropagation(); if (window.confirm('Eliminare questa iniziativa?')) onDelete(init.id); }}
              data-testid={`delete-initiative-${init.id}`}
              className="flex items-center gap-1 text-[10px] text-[#ccc] hover:text-red-500 transition-colors">
              <Trash2 size={10} /> Elimina
            </button>
          </div>
        </div>
      )}
    </div>
  );
}


function MiniKpi({ icon: Icon, label, value, color }) {
  return (
    <div className="bg-white rounded-xl border border-slate-100 p-3 shadow-sm">
      <div className="flex items-center gap-2 mb-1">
        <Icon size={14} style={{ color }} />
        <span className="text-[10px] text-[#64748B]">{label}</span>
      </div>
      <p className="text-lg font-bold text-[#1A1A1A]">{value}</p>
    </div>
  );
}

/* ═══════════════════════════════════════════════════════════════════════
   MARKETING SETUP SECTION (moved from AdminMarketing.jsx)
   ═══════════════════════════════════════════════════════════════════════ */
function MarketingSetupSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(null);

  useEffect(() => {
    adminApi.get('/admin/marketing/setup')
      .then(r => setData(r.data))
      .catch(() => toast.error('Errore caricamento profili marketing'))
      .finally(() => setLoading(false));
  }, []);

  const applyProfile = async (profileId) => {
    setApplying(profileId);
    try {
      await adminApi.post('/admin/marketing/apply', { profile_id: profileId });
      setData(d => ({ ...d, current_profile: profileId }));
      toast.success(`Profilo "${profileId}" applicato!`);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Errore');
    } finally { setApplying(null); }
  };

  if (loading) return <div className="flex items-center justify-center h-32"><Loader2 className="animate-spin text-[#D44E20]" size={24} /></div>;

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-bold text-[#1A1A1A]">Intensita Comunicazione</h2>
        <p className="text-[#64748B] text-sm mt-1">Scegli il livello di comunicazione marketing verso i tuoi clienti</p>
      </div>

      {/* Stats bar */}
      <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
        <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm">
          <div className="flex items-center gap-2 mb-1"><Users size={16} className="text-[#64748B]" /><span className="text-xs text-[#64748B]">Clienti totali</span></div>
          <p className="text-2xl font-bold text-[#1A1A1A]">{data?.total_customers || 0}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm">
          <div className="flex items-center gap-2 mb-1"><MessageCircle size={16} className="text-[#64748B]" /><span className="text-xs text-[#64748B]">Con WhatsApp</span></div>
          <p className="text-2xl font-bold text-[#1A1A1A]">{data?.wa_customers || 0}</p>
        </div>
        <div className="bg-white rounded-xl p-4 border border-slate-100 shadow-sm col-span-2 sm:col-span-1">
          <div className="flex items-center gap-2 mb-1"><Shield size={16} className="text-[#64748B]" /><span className="text-xs text-[#64748B]">Profilo attivo</span></div>
          <p className="text-2xl font-bold capitalize" style={{ color: data?.current_profile ? PROFILE_STYLES[data.current_profile]?.color : '#888' }}>
            {data?.current_profile || 'Nessuno'}
          </p>
        </div>
      </div>

      {/* Profile cards */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {data?.profiles?.map(profile => {
          const style = PROFILE_STYLES[profile.id];
          const Icon = style.icon;
          const isActive = data.current_profile === profile.id;
          const isApplying = applying === profile.id;

          return (
            <div key={profile.id}
              data-testid={`marketing-profile-${profile.id}`}
              className={`bg-gradient-to-br ${style.gradient} rounded-2xl border-2 ${isActive ? 'ring-2 ring-offset-2' : ''} shadow-sm transition-all hover:shadow-md relative overflow-hidden`}
              style={{ borderColor: isActive ? style.color : undefined, ringColor: isActive ? style.color : undefined }}>

              <div className="absolute top-3 right-3">
                <PageTooltip page="strategy" />
                <span className="text-[10px] font-bold px-2 py-0.5 rounded-full text-white" style={{ backgroundColor: style.color }}>{style.tag}</span>
              </div>
              {isActive && (
                <div className="absolute top-3 left-3">
                  <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-white text-[#1A1A1A] shadow-sm flex items-center gap-1"><Check size={10} /> Attivo</span>
                </div>
              )}

              <div className="p-5 pt-10">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-12 h-12 rounded-xl flex items-center justify-center" style={{ backgroundColor: `${style.color}20` }}>
                    <Icon size={24} style={{ color: style.color }} />
                  </div>
                  <div>
                    <p className="text-lg font-bold text-[#1A1A1A]">{profile.name}</p>
                    <p className="text-xs text-[#64748B]">{profile.subtitle}</p>
                  </div>
                </div>
                <p className="text-sm text-[#555] mb-4 leading-relaxed">{profile.description}</p>

                <div className="grid grid-cols-2 gap-2 mb-4">
                  <div className="bg-white/70 rounded-lg p-2.5 text-center">
                    <p className="text-lg font-bold text-[#1A1A1A]">{profile.automation_count}</p>
                    <p className="text-[10px] text-[#64748B]">Automazioni</p>
                  </div>
                  <div className="bg-white/70 rounded-lg p-2.5 text-center">
                    <p className="text-lg font-bold text-[#1A1A1A]">{profile.broadcast_limit}</p>
                    <p className="text-[10px] text-[#64748B]">Broadcast/mese</p>
                  </div>
                </div>

                <div className="bg-white/80 rounded-xl p-3 mb-4 border border-white">
                  <div className="flex items-center gap-1.5 mb-2">
                    <Euro size={14} className="text-[#64748B]" />
                    <span className="text-xs font-semibold text-[#64748B]">Stima costi mensili</span>
                  </div>
                  <p className="text-2xl font-bold text-[#1A1A1A]">{profile.costs.total_cost.toFixed(2)} <span className="text-sm font-normal text-[#64748B]">EUR/mese</span></p>
                  <div className="mt-2 space-y-1">
                    <div className="flex justify-between text-[11px]"><span className="text-[#64748B]">Transazionali ({profile.costs.transactional_msgs} msg)</span><span className="text-[#555] font-medium">{profile.costs.transactional_cost.toFixed(2)} EUR</span></div>
                    <div className="flex justify-between text-[11px]"><span className="text-[#64748B]">Marketing auto ({profile.costs.marketing_auto_msgs} msg)</span><span className="text-[#555] font-medium">{profile.costs.marketing_cost.toFixed(2)} EUR</span></div>
                    <div className="flex justify-between text-[11px]"><span className="text-[#64748B]">Broadcast ({profile.costs.broadcast_msgs} msg)</span><span className="text-[#555] font-medium">{profile.costs.broadcast_cost.toFixed(2)} EUR</span></div>
                  </div>
                </div>

                <details className="mb-4 group">
                  <summary className="text-xs font-semibold text-[#64748B] cursor-pointer hover:text-[#555]">Automazioni incluse ({profile.automation_count})</summary>
                  <div className="mt-2 space-y-1 pl-1">
                    {profile.automations.map(a => (
                      <div key={a} className="flex items-center gap-1.5 text-[11px]"><Check size={10} style={{ color: style.color }} /><span className="text-[#555]">{AUTOMATION_LABELS[a] || a}</span></div>
                    ))}
                  </div>
                </details>

                <button
                  onClick={() => applyProfile(profile.id)}
                  disabled={isActive || isApplying}
                  data-testid={`apply-profile-${profile.id}`}
                  className="w-full py-3 rounded-xl text-sm font-bold transition-all active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-2"
                  style={{ backgroundColor: isActive ? '#E5E5E5' : style.color, color: isActive ? '#888' : '#fff' }}>
                  {isApplying ? <><Loader2 size={16} className="animate-spin" /> Applicando...</> :
                   isActive ? <><Check size={16} /> Profilo attivo</> : 'Applica profilo'}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <div className="mt-6 bg-white rounded-2xl p-5 border border-slate-100 shadow-sm">
        <p className="text-sm font-semibold text-[#1A1A1A] mb-2">Come funziona</p>
        <ul className="text-xs text-[#64748B] space-y-1.5">
          <li>Scegliendo un profilo, le automazioni incluse verranno attivate automaticamente</li>
          <li>Il limite broadcast mensile viene aggiornato in base al profilo scelto</li>
          <li>I costi sono stime basate sui {data?.wa_customers || 0} clienti con consenso WhatsApp attivo</li>
          <li>Puoi personalizzare le singole automazioni dalla pagina Automazioni</li>
        </ul>
      </div>
    </div>
  );
}
